// The autonomy dial (Brief 4 / P3 — Govern). A four-stop authority ladder that
// sets "how much can it do before it needs me" in operator language. Governs one
// SCOPE: a single session, OR the STANDING default (every session with no
// more-specific rule — "set policy once, not per session", UC-6). Writes the
// guard (supervisor/Supervisor) rule via POST /rules; reads the ENFORCED truth from
// GET /rules (session ▸ project ▸ global resolution). Optimistic echo, reconcile
// from the server (reuses P2's pattern). Lowering protection earns a calm inline
// confirm — never a rubber-stamp.
//
// All the model + state logic lives in the host-tested `authorityModel`; this is
// the thin React shell + I/O.

import { useEffect, useReducer, useRef, useState } from "react";
import { getRules, setRule } from "../api/client";
import type { OperatorRule, RuleAuthority } from "@waytide/core";
import {
  AUTHORITY_LADDER,
  authorityLabel,
  authorityMeaning,
  defaultContactFrequency,
  dialDisplay,
  dialReducer,
  isLoweringProtection,
  ruleScopeFields,
  scopeLabel,
  type DialScope,
} from "./authorityModel";
import "./dial.css";

export function AuthorityDial({ scope }: { scope: DialScope }) {
  const [state, dispatch] = useReducer(dialReducer, {
    effective: "read_only",
    pending: null,
  });
  const [loaded, setLoaded] = useState(false);
  const [confirming, setConfirming] = useState<RuleAuthority | null>(null);
  const baseRule = useRef<OperatorRule | null>(null);

  // Primitive scope identity for the effect deps (scope is a fresh object each
  // render; these are stable).
  const kind = scope.kind;
  const threadId = scope.kind === "session" ? scope.threadId : 0;
  const projectKey = scope.kind === "project" ? scope.projectKey : "";

  useEffect(() => {
    let alive = true;
    // Each scope reads the rule in force for it: a session reads its resolved
    // rule (session ▸ project ▸ global); a project reads project ▸ global; the
    // standing default reads the global rule.
    const read =
      kind === "session"
        ? getRules(String(threadId), String(threadId))
        : kind === "project"
          ? getRules(projectKey)
          : getRules();
    read
      .then((rs) => {
        if (!alive) return;
        baseRule.current = rs.supervisor;
        dispatch({ type: "confirm", authority: rs.supervisor.authority });
        setLoaded(true);
      })
      .catch(() => alive && setLoaded(true));
    return () => {
      alive = false;
    };
  }, [kind, threadId, projectKey]);

  function apply(authority: RuleAuthority) {
    setConfirming(null);
    dispatch({ type: "set", authority }); // optimistic echo
    setRule({
      ...ruleScopeFields(scope),
      operator: "supervisor",
      enabled: true,
      watch_for: baseRule.current?.watch_for ?? "",
      authority,
      contact_frequency: defaultContactFrequency(authority),
    })
      .then((saved) => dispatch({ type: "confirm", authority: saved.authority }))
      .catch(() => dispatch({ type: "fail" }));
  }

  function choose(authority: RuleAuthority) {
    const current = dialDisplay(state).authority;
    if (authority === current) {
      setConfirming(null);
      return;
    }
    // Lowering protection (more autonomy) is the one move that is not
    // rubber-stampable — a calm inline confirm, never a per-action prompt.
    if (isLoweringProtection(current, authority)) {
      setConfirming(authority);
      return;
    }
    apply(authority);
  }

  const display = dialDisplay(state);

  return (
    <div
      className="wt-dial"
      data-pending={display.pending ? "true" : "false"}
      data-authority={display.authority}
    >
      <div className="wt-dial__caption">
        <span className="wt-dial__label">
          How much can it do before it needs me
        </span>
        <span className="wt-dial__scope">{scopeLabel(scope)}</span>
      </div>
      <div className="wt-dial__ladder" role="radiogroup" aria-label="Authority">
        {AUTHORITY_LADDER.map((a) => (
          <button
            key={a}
            type="button"
            role="radio"
            aria-checked={display.authority === a}
            className="wt-dial__stop"
            data-active={display.authority === a ? "true" : "false"}
            onClick={() => choose(a)}
            disabled={!loaded}
            title={authorityMeaning(a)}
          >
            {authorityLabel(a)}
          </button>
        ))}
      </div>
      <p className="wt-dial__meaning">{authorityMeaning(display.authority)}</p>
      {confirming ? (
        <div className="wt-dial__confirm" role="group">
          <span>
            Give it more room — {authorityLabel(confirming).toLowerCase()}?
          </span>
          <button
            type="button"
            className="wt-dial__confirm-yes"
            onClick={() => apply(confirming)}
          >
            Confirm
          </button>
          <button
            type="button"
            className="wt-dial__confirm-no"
            onClick={() => setConfirming(null)}
          >
            Keep as is
          </button>
        </div>
      ) : null}
    </div>
  );
}
